/**
 * F1b — React hook that schedules `drainOnce` whenever connectivity is
 * likely to have changed: window 'online' event, window focus, and a
 * 60-second interval while online. Mount once near the app root (in
 * `main.tsx`).
 *
 * Also registers the 'save-back' replayer via `setKindReplayer` so
 * queued saveBacks know how to replay. New kinds register from their own
 * SPA-side modules; the leaf queue stays kind-agnostic.
 *
 * Leaf module — only React + the leaf queue + leaf sharepoint helpers.
 */

import { useEffect, useRef } from 'react';
import { drainOnce, setKindReplayer } from './drainWorker';
import { putContent } from '../graph/sharepoint';

const DRAIN_INTERVAL_MS = 60_000;

interface SaveBackPayload {
  body: string;
}

let replayersRegistered = false;
function ensureReplayersRegistered(): void {
  if (replayersRegistered) return;
  replayersRegistered = true;
  setKindReplayer('save-back', async (row) => {
    const payload = row.payload as SaveBackPayload | undefined;
    if (!payload || typeof payload.body !== 'string') {
      // Bad row — should never happen (validated at enqueue). Throw fatal so
      // the drain worker drops it.
      throw Object.assign(new Error('save-back row missing body'), { statusCode: 400 });
    }
    await putContent(
      row.targetRef.driveId,
      row.targetRef.itemId,
      payload.body,
      row.baseEtag,
    );
  });
  // 'create-link' and 'invite' replayers will land with F3+ when those
  // operations are wired into the queue.
}

export function useLocalEditQueueDrain(): void {
  const drainingRef = useRef(false);

  useEffect(() => {
    ensureReplayersRegistered();
    if (typeof window === 'undefined') return;

    const trigger = async () => {
      if (drainingRef.current) return;
      drainingRef.current = true;
      try {
        await drainOnce();
      } finally {
        drainingRef.current = false;
      }
    };

    // Initial drain (covers reload-with-queued-items).
    void trigger();

    const onOnline = () => void trigger();
    const onFocus = () => {
      if (navigator.onLine !== false) void trigger();
    };
    const tick = window.setInterval(() => {
      if (navigator.onLine !== false) void trigger();
    }, DRAIN_INTERVAL_MS);

    window.addEventListener('online', onOnline);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('focus', onFocus);
      window.clearInterval(tick);
    };
  }, []);
}
